import { useEffect, useState, useMemo, createContext, useRef, useCallback } from 'react'
import { useHistory, useLocation } from "react-router-dom"
import Board, { addColumn } from '@asseinfo/react-kanban'
import '@asseinfo/react-kanban/dist/styles.css'

import {
    Button,
    Box,
    IconButton,
    InputAdornment,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Stack,
    Alert,
    FormControl,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Divider,
    Typography,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Drawer,
    FormGroup,
    FormControlLabel,
    Switch,
    Checkbox,
    ListItemText,
} from '@mui/material'

import ImportLeadsModal from './ImportLeadsModal';
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import useInjectComponent from '../../utils/useInjectComponent'
import NewOpportunityButton from './opportunity-button'
import EditableTitle from './editable-title'
import SingleCard, { getCustomFieldVisibilityKey } from './single-card'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import TableRowsIcon from '@mui/icons-material/TableRows'
import SettingsIcon from '@mui/icons-material/Settings'
import EditIcon from '@mui/icons-material/Edit'
import DownloadIcon from '@mui/icons-material/Download'
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from "@mui/icons-material/MoreVert"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import TuneIcon from "@mui/icons-material/Tune"

import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"

import {
    DataGridPro,
    ptBR,
    gridClasses,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarColumnsButton
} from '@mui/x-data-grid-pro'

import {
    newCard, moveColumn, moveCard, changeColumnName,
    removeColumn, loadKanban, createColumn,
    changeColumnNameCallback, moveColumnCallback,
    moveCardCallback, calcBoardSummary, formatCurrency,
    deleteCard as deleteCardFunction, removeColumnCallback,
    deleteCardCallback, changeColumnDescription, loadColumnCards,
    changeColumnDescriptionCallback, moveAllCards, moveJourneyColumn,
    buildJourneyOpportunityParams,
    loadJourneyOpportunities, JOURNEY_COLUMN_PAGE_SIZE,
    JOURNEY_LIST_PAGE_SIZE
} from './functions'

import './styles.css'
import Opportunity from './opportunity'

import dayjs from 'dayjs'
import Loading from '../Loading'

import { getSession } from "../../auth"
import api from "../../api"
import UserPermissions from '../User/Permissions'
import TransferOpportunityDialog from "./TransferOpportunityDialog";
import useNotification from "../Notification";
import { validateEmail } from '../Report/NewDetailedRegister/report_export_data'

import JourneyAutomations, { AutomationItem } from './Automations'
import LazyRender from '../../utils/lazyRender';
import WebForms from '../Webforms';
import KwikMarkdown from '../../utils/kwikmarkdown';
import {
    STANDARD_OPPORTUNITY_FIELDS,
    getStandardFieldSettings,
    getStandardFieldLabel,
    isStandardFieldRequired,
    isStandardFieldSummaryVisible,
    setStandardFieldSettingValue,
    normalizeStandardFieldSettingsForSave,
} from './standard-fields';

export const JourneyContext = createContext()

const LEGACY_SUMMARY_CUSTOM_FIELDS_KEY = "customFields"

const getCardFieldsStorageKey = (journey) => `journey-card-fields-${journey?.id || 'default'}`

const getAvailableCustomCardSummaryFields = (journey) => (
    (Array.isArray(journey?.custom_fields) ? journey.custom_fields : [])
        .filter(field => field?.show_in_summary && field?.id !== null && field?.id !== undefined)
        .map(field => ({
            key: getCustomFieldVisibilityKey(field.id),
            label: `Campo customizado: ${field.name}`,
            required: false,
            customField: true,
        }))
)

const getAvailableCardSummaryFields = (journey) => (
    STANDARD_OPPORTUNITY_FIELDS
        .filter(field => field.summary && isStandardFieldSummaryVisible(journey, field.key))
        .map(field => ({
            key: field.key,
            label: getStandardFieldLabel(journey, field.key),
            required: isStandardFieldRequired(journey, field.key),
        }))
)

const normalizeVisibleFields = (storedFields, availableFields) => {
    const normalized = {}

    availableFields.forEach(field => {
        if (field.required) {
            normalized[field.key] = true
            return
        }

        if (Object.prototype.hasOwnProperty.call(storedFields || {}, field.key)) {
            normalized[field.key] = storedFields[field.key] !== false
            return
        }

        normalized[field.key] = !(
            field.customField &&
            storedFields?.[LEGACY_SUMMARY_CUSTOM_FIELDS_KEY] === false
        )
    })

    return normalized
}

const BOARD_DRAG_SCROLL_BLOCKED_SELECTOR = [
    ".react-kanban-column",
    ".journey-card",
    ".new-kanban-column-btn",
    "button",
    "a",
    "input",
    "textarea",
    "select",
    "[role='button']",
    "[contenteditable='true']",
    ".MuiButtonBase-root",
    ".MuiInputBase-root",
    ".MuiSelect-select",
].join(",")

const canStartBoardDragScroll = (event, container) => {
    if (!container || event.button !== 0) return false
    if (!(event.target instanceof Element)) return false
    if (!container.contains(event.target)) return false

    const scrollbarHeight = container.offsetHeight - container.clientHeight
    if (scrollbarHeight > 0) {
        const { bottom } = container.getBoundingClientRect()
        if (event.clientY >= bottom - scrollbarHeight) return false
    }

    return !event.target.closest(BOARD_DRAG_SCROLL_BLOCKED_SELECTOR)
}

const COLUMN_SCROLL_BOTTOM_THRESHOLD = 80
const CARD_DRAG_AUTO_SCROLL_EDGE_SIZE = 120
const CARD_DRAG_AUTO_SCROLL_MAX_SPEED = 14
const NO_TAGS_FILTER = "__no_tags__"

const CARD_DRAG_AUTO_SCROLL_BLOCKED_SELECTOR = [
    "button",
    "a",
    "input",
    "textarea",
    "select",
    "[role='button']",
    "[contenteditable='true']",
    ".MuiButtonBase-root",
    ".MuiInputBase-root",
    ".MuiSelect-select",
].join(",")

const canStartCardDragAutoScroll = (event, container) => {
    if (!container || event.button !== 0) return false
    if (!(event.target instanceof Element)) return false
    if (!container.contains(event.target)) return false
    const cardElement = event.target.closest(".journey-card")
    if (!cardElement) return false

    const blockedElement = event.target.closest(CARD_DRAG_AUTO_SCROLL_BLOCKED_SELECTOR)
    return !blockedElement || !cardElement.contains(blockedElement)
}

const getCardDragAutoScrollSpeed = (pointerX, container) => {
    if (!Number.isFinite(pointerX)) return 0

    const rect = container.getBoundingClientRect()
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    const visibleLeft = Math.max(rect.left, 0)
    const visibleRight = Math.min(rect.right, viewportWidth)
    const leftDistance = pointerX - visibleLeft
    const rightDistance = visibleRight - pointerX

    if (leftDistance < CARD_DRAG_AUTO_SCROLL_EDGE_SIZE) {
        const ratio = Math.min(1, Math.max(0, (CARD_DRAG_AUTO_SCROLL_EDGE_SIZE - leftDistance) / CARD_DRAG_AUTO_SCROLL_EDGE_SIZE))
        return -Math.ceil(ratio * CARD_DRAG_AUTO_SCROLL_MAX_SPEED)
    }

    if (rightDistance < CARD_DRAG_AUTO_SCROLL_EDGE_SIZE) {
        const ratio = Math.min(1, Math.max(0, (CARD_DRAG_AUTO_SCROLL_EDGE_SIZE - rightDistance) / CARD_DRAG_AUTO_SCROLL_EDGE_SIZE))
        return Math.ceil(ratio * CARD_DRAG_AUTO_SCROLL_MAX_SPEED)
    }

    return 0
}

const canScrollHorizontally = (element, speed) => {
    if (!element) return false

    const maxScrollLeft = element.scrollWidth - element.clientWidth
    if (maxScrollLeft <= 0) return false

    return speed < 0
        ? element.scrollLeft > 0
        : element.scrollLeft < maxScrollLeft
}

const scrollHorizontalBoard = (container, speed) => {
    const candidates = [container]
    let parent = container?.parentElement

    while (parent && parent !== document.body) {
        candidates.push(parent)
        parent = parent.parentElement
    }

    const documentScroller = document.scrollingElement || document.documentElement
    if (documentScroller) {
        candidates.push(documentScroller)
    }

    const target = candidates.find(element => canScrollHorizontally(element, speed))
    if (target) {
        target.scrollLeft += speed
        target.dispatchEvent(new Event("scroll", { bubbles: true }))
        window.dispatchEvent(new Event("scroll"))
    }
}

const stopCardDragAutoScroll = (cardDragAutoScroll) => {
    cardDragAutoScroll.current.active = false
    cardDragAutoScroll.current.moved = false

    if (cardDragAutoScroll.current.frame) {
        window.cancelAnimationFrame(cardDragAutoScroll.current.frame)
        cardDragAutoScroll.current.frame = null
    }
}

const CardDragAutoScroll = ({ dragging, boardScrollRef, cardDragAutoScroll, dropTargetHighlightRef, children }) => {
    useEffect(() => {
        if (!dragging) return

        const container = boardScrollRef.current
        container?.classList.add("card-dragging")

        cardDragAutoScroll.current = {
            ...cardDragAutoScroll.current,
            active: true,
            moved: true,
        }

        const scrollBoard = () => {
            cardDragAutoScroll.current.frame = null

            const container = boardScrollRef.current
            if (!container || !cardDragAutoScroll.current.active) return

            const speed = getCardDragAutoScrollSpeed(cardDragAutoScroll.current.pointerX, container)
            if (speed) {
                scrollHorizontalBoard(container, speed)
            }

            const dropTarget = getDropTargetInfo(
                container,
                cardDragAutoScroll.current.pointerX,
                cardDragAutoScroll.current.pointerY
            )
            updateDropTargetHighlight(dropTarget, dropTargetHighlightRef)

            cardDragAutoScroll.current.frame = window.requestAnimationFrame(scrollBoard)
        }

        if (cardDragAutoScroll.current.frame) {
            window.cancelAnimationFrame(cardDragAutoScroll.current.frame)
        }
        cardDragAutoScroll.current.frame = window.requestAnimationFrame(scrollBoard)

        return () => {
            stopCardDragAutoScroll(cardDragAutoScroll)
            clearDropTargetHighlight(dropTargetHighlightRef)
            window.requestAnimationFrame(() => container?.classList.remove("card-dragging"))
        }
    }, [dragging, boardScrollRef, cardDragAutoScroll, dropTargetHighlightRef])

    return children
}

const OPPORTUNITY_STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Ativa" },
    { value: "QUALIFIED", label: "Qualificada" },
    { value: "CONQUERED", label: "Conquistada" },
    { value: "LOST", label: "Perdida" },
    { value: "INACTIVE", label: "Inativa" },
]

const CUSTOM_FIELD_TRUE_VALUES = ["1", "true", "yes", "sim", "on"]

const getCustomFieldTypeLabel = (fieldType) => (
    fieldType === "text"
        ? "Texto"
        : fieldType === "textarea"
        ? "Texto longo"
        : fieldType === "number"
        ? "Número"
        : fieldType === "select"
        ? "Lista"
        : fieldType === "date"
        ? "Data"
        : fieldType === "checkbox" || fieldType === "boolean"
        ? "Sim/Não"
        : fieldType
)

const isBooleanCustomField = (field) => ["checkbox", "boolean"].includes(field?.field_type)

const isTruthyCustomFieldValue = (value) => value === true || value === 1 || CUSTOM_FIELD_TRUE_VALUES.includes(String(value || "").trim().toLowerCase())

const getCustomFieldFilterOptions = (journey) => (
    (Array.isArray(journey?.custom_fields) ? journey.custom_fields : [])
        .filter(field => field?.id !== null && field?.id !== undefined)
        .map(field => ({
            ...field,
            label: `${field.name} (${getCustomFieldTypeLabel(field.field_type)})`,
        }))
)

const isCustomFieldFilterActive = (filter, customFields) => {
    const field = customFields.find(item => String(item.id) === String(filter?.field))
    if (!field) return false
    if (isBooleanCustomField(field)) return filter.value === "true" || filter.value === "false"
    return filter.value !== null && filter.value !== undefined && String(filter.value).trim() !== ""
}

const normalizeCustomFieldFilters = (filters, customFields) => (
    (Array.isArray(filters) ? filters : [])
        .filter(filter => isCustomFieldFilterActive(filter, customFields))
        .map(filter => ({
            field: filter.field,
            value: filter.value,
        }))
)

const getCardCustomFieldValue = (card, fieldId) => {
    const liveField = Array.isArray(card?.custom_fields)
        ? card.custom_fields.find(field => String(field.field) === String(fieldId))
        : null

    if (liveField) return liveField.value

    const snapshot = card?.custom_fields_snapshot
    if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) {
        return snapshot[String(fieldId)]?.value
    }

    return null
}

const matchesCustomFieldFilters = (card, filters, customFields) => (
    filters.every(filter => {
        const field = customFields.find(item => String(item.id) === String(filter.field))
        if (!field) return true

        const fieldValue = getCardCustomFieldValue(card, filter.field)

        if (isBooleanCustomField(field)) {
            const expected = filter.value === "true"
            return expected ? isTruthyCustomFieldValue(fieldValue) : !isTruthyCustomFieldValue(fieldValue)
        }

        if (fieldValue === null || fieldValue === undefined) return false

        const normalizedValue = String(fieldValue)
        const normalizedFilterValue = String(filter.value || "")

        if (field.field_type === "text" || field.field_type === "textarea") {
            return normalizedValue.toLowerCase().includes(normalizedFilterValue.toLowerCase())
        }

        return normalizedValue === normalizedFilterValue
    })
)

const JOURNEY_FILTER_SELECT_MENU_PROPS = {
    className: "journey-filter-select-menu",
    PaperProps: {
        className: "journey-filter-select-menu-paper",
    },
}

const JOURNEY_FILTER_MENU_ITEM_SX = {
    "&.Mui-selected": {
        backgroundColor: "rgba(25, 118, 210, 0.08)",
    },
    "&.Mui-selected:hover": {
        backgroundColor: "rgba(25, 118, 210, 0.14)",
    },
}

const getColumnIdFromColumnElement = (columnElement) => {
    const draggableId = columnElement?.getAttribute('data-rbd-draggable-id')
    if (draggableId) return Number(draggableId.replace('column-draggable-', ''))

    const droppableId = columnElement?.querySelector('[data-rbd-droppable-id]')?.getAttribute('data-rbd-droppable-id')
    return Number(droppableId || 0)
}

const getColumnCardsScrollElement = (columnElement) => {
    const directChild = Array.from(columnElement?.children || [])
        .find(child => child.getAttribute('data-rbd-droppable-id'))

    return directChild || columnElement?.querySelector('[data-rbd-droppable-id]')
}

const getDropTargetInfo = (boardElement, x, y) => {
    if (!boardElement || !Number.isFinite(x) || !Number.isFinite(y)) {
        return { columnId: null, columnElement: null, position: 0, cardElement: null }
    }

    const columnElement = Array.from(boardElement.getElementsByClassName("react-kanban-column"))
        .find(el => {
            const rect = el.getBoundingClientRect()
            return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
        })

    if (!columnElement) {
        return { columnId: null, columnElement: null, position: 0, cardElement: null }
    }

    const scrollElement = getColumnCardsScrollElement(columnElement)
    const cardElements = scrollElement ? Array.from(scrollElement.querySelectorAll(".journey-card")) : []
    const targetIndex = cardElements.findIndex(cardElement => {
        const rect = cardElement.getBoundingClientRect()
        return y < rect.top + rect.height / 2
    })

    return {
        columnId: getColumnIdFromColumnElement(columnElement),
        columnElement,
        position: targetIndex === -1 ? cardElements.length : targetIndex,
        cardElement: targetIndex === -1 ? null : cardElements[targetIndex],
    }
}

const DROP_TARGET_GAP_CLASS = "journey-drop-target-gap"

const updateDropTargetHighlight = (dropTarget, highlightRef) => {
    const { cardElement } = dropTarget

    if (highlightRef.current.cardElement && highlightRef.current.cardElement !== cardElement) {
        highlightRef.current.cardElement.classList.remove(DROP_TARGET_GAP_CLASS)
    }

    if (cardElement) cardElement.classList.add(DROP_TARGET_GAP_CLASS)

    highlightRef.current = { cardElement: cardElement || null }
}

const clearDropTargetHighlight = (highlightRef) => {
    if (highlightRef.current.cardElement) {
        highlightRef.current.cardElement.classList.remove(DROP_TARGET_GAP_CLASS)
    }
    highlightRef.current = { cardElement: null }
}

function KanbanHeader({
    close,
    search,
    setSearch,
    sellers,
    setSellers,
    selectedTags,
    setSelectedTags,
    selectedStatuses,
    setSelectedStatuses,
    customFieldFilters,
    setCustomFieldFilters,
    allUsers,
    selectedJourney,
    enterpriseId,
    board,
    setRequiredFields,
    setJourneyCustomFields,
    standardFieldSettings,
    setStandardFieldSettings,
    setShowRequiredFieldsModal,
    viewMode,
    setViewMode,
    setShowImportLeads,
    setShowForms,
    groups,
    availableCardFields,
    visibleFields,
    setVisibleFields,
    cardFieldsStorageKey,
}) {

    const [settingsAnchor, setSettingsAnchor] = useState(null)
    const [filterOpen, setFilterOpen] = useState(false)
    const [showExportEmailDialog, setShowExportEmailDialog] = useState(false)
    const [exportEmail, setExportEmail] = useState('')
    const [exportSending, setExportSending] = useState(false)
    const journeyUsers = useMemo(() => {
        const usersById = new Map()

        const getUserId = (user) => {
            if (user && typeof user === "object") return user.id
            return user
        }

        const getUserLabel = (user) => {
            if (!user || typeof user !== "object") return String(user || "")
            const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim()
            return fullName || user.name || user.username || user.email || String(user.id || "")
        }

        const addUser = (user) => {
            const id = getUserId(user)
            if (id === null || id === undefined || id === "") return

            const key = String(id)
            const current = usersById.get(key) || {}
            usersById.set(key, {
                ...current,
                ...(user && typeof user === "object" ? user : {}),
                id,
                label: getUserLabel(user) || current.label || String(id),
            })
        }

        const journeyUserRefs = [
            ...(Array.isArray(selectedJourney?.users) ? selectedJourney.users : []),
            ...(Array.isArray(selectedJourney?.users_serialized) ? selectedJourney.users_serialized : []),
            ...(Array.isArray(board?.users) ? board.users : []),
            ...(Array.isArray(board?.users_serialized) ? board.users_serialized : []),
        ]
        const journeyUserIds = new Set(journeyUserRefs.map(getUserId).filter(id => id !== null && id !== undefined).map(String))

        journeyUserRefs.forEach(addUser)

        const availableUsers = Array.isArray(allUsers) ? allUsers : []
        availableUsers.forEach(user => {
            const id = getUserId(user)
            if (journeyUserIds.size === 0 || journeyUserIds.has(String(id))) {
                addUser(user)
            }
        })

        const boardColumns = board?.columns || []
        boardColumns.forEach(column => {
            (column.cards || []).forEach(card => {
                if (card.owner_id) {
                    addUser({
                        id: card.owner_id,
                        name: card.owner_name,
                    })
                }
            })
        })

        return Array.from(usersById.values()).sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")))
    }, [allUsers, selectedJourney?.users, selectedJourney?.users_serialized, board?.users, board?.users_serialized, board?.columns])
    const journeyTags = useMemo(() => {
        const tagsById = new Map()

        const addTag = (tag, usageCount = 0, cardUsage = 0) => {
            if (!tag) return

            const value = tag.id ?? tag.name
            if (value === null || value === undefined || value === "") return

            const key = String(value)
            const current = tagsById.get(key) || {
                ...tag,
                id: value,
                usage_count: 0,
                card_usage_count: 0,
            }

            tagsById.set(key, {
                ...current,
                ...tag,
                id: value,
                usage_count: Math.max(Number(current.usage_count || 0), Number(usageCount || 0)),
                card_usage_count: Number(current.card_usage_count || 0) + Number(cardUsage || 0),
            })
        }

        const baseTagLists = [selectedJourney?.tags, board?.tags]
        baseTagLists.forEach(tags => {
            if (!Array.isArray(tags)) return
            tags.forEach(tag => addTag(tag, tag.usage_count))
        })

        const boardColumns = board?.columns || []
        boardColumns.forEach(column => {
            (column.cards || []).forEach(card => {
                (card.tags || []).forEach(tag => addTag(tag, 0, 1))
            })
        })

        return Array.from(tagsById.values()).sort((a, b) => (
            Number(b.usage_count || b.card_usage_count || 0) -
            Number(a.usage_count || a.card_usage_count || 0)
        ) || String(a.name || "").localeCompare(String(b.name || "")))
    }, [selectedJourney?.tags, board?.tags, board?.columns])
    const topJourneyTags = journeyTags.slice(0, 3)
    const topJourneyTagIds = topJourneyTags.map(tag => String(tag.id))
    const remainingJourneyTags = journeyTags.filter(tag => !topJourneyTagIds.includes(String(tag.id)))
    const selectedTagIds = selectedTags.map(String)
    const selectedRemainingTags = selectedTags.filter(value => remainingJourneyTags.some(tag => String(tag.id) === String(value)))
    const customFieldOptions = useMemo(() => getCustomFieldFilterOptions(board || selectedJourney), [board, selectedJourney])
    const sellerFilterLabel = getStandardFieldLabel(board || selectedJourney, "owner")
    const activeCustomFieldFilters = useMemo(
        () => normalizeCustomFieldFilters(customFieldFilters, customFieldOptions),
        [customFieldFilters, customFieldOptions]
    )
    const activeFilterCount = [
        search.trim(),
        sellers.length,
        selectedTags.length,
        selectedStatuses.length,
        activeCustomFieldFilters.length,
    ].filter(Boolean).length

    const handleVisibleFieldToggle = (field) => {
        if (field.required) return

        const updated = {
            ...visibleFields,
            [field.key]: visibleFields[field.key] === false,
        }

        setVisibleFields(updated)
        localStorage.setItem(cardFieldsStorageKey, JSON.stringify(updated))
    }

    const toggleTagFilter = (value) => {
        const normalizedValue = String(value)
        setSelectedTags(prev => {
            const selectedValues = prev.map(String)
            if (selectedValues.includes(normalizedValue)) {
                return prev.filter(item => String(item) !== normalizedValue)
            }
            return [...prev, value]
        })
    }

    const addCustomFieldFilter = () => {
        setCustomFieldFilters(prev => ([
            ...prev,
            {
                id: `${Date.now()}-${prev.length}`,
                field: customFieldOptions[0]?.id || "",
                value: "",
            }
        ]))
    }

    const updateCustomFieldFilter = (filterId, values) => {
        setCustomFieldFilters(prev => prev.map(filter => {
            if (filter.id !== filterId) return filter
            return {
                ...filter,
                ...values,
            }
        }))
    }

    const removeCustomFieldFilter = (filterId) => {
        setCustomFieldFilters(prev => prev.filter(filter => filter.id !== filterId))
    }

    const renderCustomFieldFilterValue = (filter) => {
        const field = customFieldOptions.find(item => String(item.id) === String(filter.field))
        if (!field) return null

        if (isBooleanCustomField(field)) {
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel>Valor</InputLabel>
                    <Select
                        value={filter.value}
                        label="Valor"
                        MenuProps={JOURNEY_FILTER_SELECT_MENU_PROPS}
                        onChange={(event) => updateCustomFieldFilter(filter.id, { value: event.target.value })}
                    >
                        <MenuItem value="true">Sim</MenuItem>
                        <MenuItem value="false">Não</MenuItem>
                    </Select>
                </FormControl>
            )
        }

        if (field.field_type === "select") {
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel>Valor</InputLabel>
                    <Select
                        value={filter.value}
                        label="Valor"
                        MenuProps={JOURNEY_FILTER_SELECT_MENU_PROPS}
                        onChange={(event) => updateCustomFieldFilter(filter.id, { value: event.target.value })}
                    >
                        {(field.options || []).map(option => (
                            <MenuItem key={option.id || option.value} value={option.value}>
                                {option.label || option.value}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        }

        return (
            <TextField
                size="small"
                fullWidth
                label={field.field_type === "text" || field.field_type === "textarea" ? "Contém" : "Valor"}
                type={field.field_type === "number" ? "number" : field.field_type === "date" ? "date" : "text"}
                value={filter.value}
                onChange={(event) => updateCustomFieldFilter(filter.id, { value: event.target.value })}
                InputLabelProps={field.field_type === "date" ? { shrink: true } : undefined}
            />
        )
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1,
                width: "100%",
                whiteSpace: "nowrap",
                mt: 1
            }}
        >

            {/* LEFT: BACK */}
            <Button
                onClick={close}
                startIcon={<ArrowBackIcon />}
                sx={{
                    minWidth: "auto",
                    whiteSpace: "nowrap"
                }}
            >
                Voltar
            </Button>

            {/* TITLE */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    height: 40, // matches MUI small button height
                    borderRadius: 1, // matches default MUI button radius
                    backgroundColor: "rgba(25, 118, 210, 0.08)", // subtle primary tint
                    border: "1px solid rgba(25, 118, 210, 0.2)",
                    whiteSpace: "nowrap",
                    flexShrink: 0
                }}
            >
                <Typography
                    sx={{
                        fontSize: 12,
                        color: "primary.main",
                        fontWeight: 500,
                        lineHeight: 1
                    }}
                >
                    Jornada
                </Typography>

                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: 1,
                        color: "text.primary"
                    }}
                >
                    {selectedJourney.name}
                </Typography>
            </Box>

            <Badge
                color="primary"
                badgeContent={activeFilterCount}
                invisible={!activeFilterCount}
                sx={{ flexShrink: 0 }}
            >
                <Button
                    variant={activeFilterCount ? "contained" : "outlined"}
                    startIcon={<TuneIcon />}
                    onClick={() => setFilterOpen(true)}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    Filtros
                </Button>
            </Badge>

            <Drawer
                className="journey-filter-drawer"
                anchor="right"
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                ModalProps={{
                    className: "journey-filter-drawer-modal",
                }}
                PaperProps={{
                    className: "journey-filter-drawer-paper",
                    sx: {
                        width: { xs: "100%", sm: 360 },
                        maxWidth: "100%",
                    }
                }}
            >
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Filtros
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearch("")
                                setSellers([])
                                setSelectedTags([])
                                setSelectedStatuses([])
                                setCustomFieldFilters([])
                            }}
                            disabled={!activeFilterCount}
                        >
                            Limpar
                        </Button>
                    </Box>

                    <TextField
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        label="Palavra-chave"
                        placeholder="Buscar oportunidade..."
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" fullWidth>
                        <InputLabel>{sellerFilterLabel}</InputLabel>
                        <Select
                            multiple
                            value={sellers}
                            label={sellerFilterLabel}
                            MenuProps={JOURNEY_FILTER_SELECT_MENU_PROPS}
                            onChange={({ target }) => {
                                if (target.value.includes("__select_all__")) {
                                    setSellers(journeyUsers.map(u => u.id));
                                } else if (target.value.includes("__select_none__")) {
                                    setSellers([]);
                                } else {
                                    setSellers(target.value);
                                }
                            }}
                            renderValue={(selected) => {
                                if (!selected.length) return "Todos"
                                return selected.map(id => {
                                    const user = journeyUsers.find(u => String(u.id) === String(id))
                                    return user?.label || id
                                }).join(", ")
                            }}
                        >
                            <MenuItem value="__select_all__">Todos</MenuItem>
                            <MenuItem value="__select_none__">Nenhum</MenuItem>
                            <Divider />

                            {journeyUsers.map(u => (
                                <MenuItem
                                    key={u.id}
                                    value={u.id}
                                    selected={sellers.map(String).includes(String(u.id))}
                                    sx={JOURNEY_FILTER_MENU_ITEM_SX}
                                >
                                    <ListItemText primary={u.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block", mb: 1 }}>
                            Marcadores
                        </Typography>

                        <Stack spacing={1}>
                            <Chip
                                clickable
                                label="Sem marcadores"
                                onClick={() => toggleTagFilter(NO_TAGS_FILTER)}
                                sx={{
                                    justifyContent: "flex-start",
                                    borderRadius: 1,
                                    width: "100%",
                                    backgroundColor: "#e0e0e0",
                                    border: selectedTagIds.includes(NO_TAGS_FILTER) ? "2px solid #1976d2" : "1px solid rgba(0, 0, 0, 0.2)",
                                }}
                            />

                            {topJourneyTags.map(tag => (
                                <Chip
                                    key={tag.id}
                                    clickable
                                    label={tag.name}
                                    onClick={() => toggleTagFilter(tag.id)}
                                    variant={selectedTagIds.includes(String(tag.id)) ? "filled" : "outlined"}
                                    sx={{
                                        justifyContent: "flex-start",
                                        borderRadius: 1,
                                        width: "100%",
                                        backgroundColor: tag.color,
                                        border: selectedTagIds.includes(String(tag.id)) ? "2px solid #1976d2" : "1px solid transparent",
                                        opacity: selectedTagIds.includes(String(tag.id)) ? 1 : .75,
                                        "&:hover": {
                                            backgroundColor: tag.color,
                                            opacity: 1,
                                        },
                                    }}
                                />
                            ))}

                            {remainingJourneyTags.length > 0 && (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Mais marcadores</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedRemainingTags}
                                        label="Mais marcadores"
                                        MenuProps={JOURNEY_FILTER_SELECT_MENU_PROPS}
                                        onChange={({ target }) => {
                                            const remainingIds = remainingJourneyTags.map(tag => String(tag.id))
                                            const selectedOutsideSelect = selectedTags.filter(value => !remainingIds.includes(String(value)))
                                            setSelectedTags([...selectedOutsideSelect, ...target.value])
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected.length) return "Selecionar marcadores"
                                            return selected.map(id => remainingJourneyTags.find(tag => String(tag.id) === String(id))?.name || id).join(", ")
                                        }}
                                    >
                                        {remainingJourneyTags.map(tag => (
                                            <MenuItem
                                                key={tag.id}
                                                value={tag.id}
                                                selected={selectedTagIds.includes(String(tag.id))}
                                                sx={JOURNEY_FILTER_MENU_ITEM_SX}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 14,
                                                        height: 14,
                                                        borderRadius: .5,
                                                        backgroundColor: tag.color,
                                                        mr: 1,
                                                        border: "1px solid rgba(0, 0, 0, 0.12)",
                                                    }}
                                                />
                                                <ListItemText primary={tag.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Stack>
                    </Box>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            multiple
                            value={selectedStatuses}
                            label="Status"
                            MenuProps={JOURNEY_FILTER_SELECT_MENU_PROPS}
                            onChange={({ target }) => setSelectedStatuses(target.value)}
                            renderValue={(selected) => {
                                if (!selected.length) return "Todos"
                                return selected.map(value => OPPORTUNITY_STATUS_OPTIONS.find(status => status.value === value)?.label || value).join(", ")
                            }}
                        >
                            {OPPORTUNITY_STATUS_OPTIONS.map(status => (
                                <MenuItem
                                    key={status.value}
                                    value={status.value}
                                    selected={selectedStatuses.includes(status.value)}
                                    sx={JOURNEY_FILTER_MENU_ITEM_SX}
                                >
                                    <ListItemText primary={status.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {customFieldOptions.length > 0 && (
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                    Campos customizados
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addCustomFieldFilter}
                                >
                                    Adicionar
                                </Button>
                            </Box>

                            <Stack spacing={1.25}>
                                {customFieldFilters.map((filter) => {
                                    const selectedField = customFieldOptions.find(item => String(item.id) === String(filter.field))

                                    return (
                                        <Box
                                            key={filter.id}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                                p: 1,
                                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                <FormControl size="small" fullWidth>
                                                    <InputLabel>Campo</InputLabel>
                                                    <Select
                                                        value={filter.field}
                                                        label="Campo"
                                                        MenuProps={JOURNEY_FILTER_SELECT_MENU_PROPS}
                                                        onChange={(event) => updateCustomFieldFilter(filter.id, { field: event.target.value, value: "" })}
                                                        renderValue={(selected) => {
                                                            const field = customFieldOptions.find(item => String(item.id) === String(selected))
                                                            return field?.label || "Selecione"
                                                        }}
                                                    >
                                                        {customFieldOptions.map(field => (
                                                            <MenuItem
                                                                key={field.id}
                                                                value={field.id}
                                                                selected={String(filter.field) === String(field.id)}
                                                                sx={JOURNEY_FILTER_MENU_ITEM_SX}
                                                            >
                                                                <ListItemText primary={field.name} secondary={getCustomFieldTypeLabel(field.field_type)} />
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeCustomFieldFilter(filter.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>

                                            {selectedField && renderCustomFieldFilterValue(filter)}
                                        </Box>
                                    )
                                })}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Drawer>

            {/* PUSH RIGHT */}
            <Box sx={{ flex: 1 }} />

            {/* ACTIONS RIGHT */}
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>

                <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setShowImportLeads(true)}
                >
                    Importar
                </Button>

                {groups.isSupervisor && (
                    <Button
                        variant="contained"
                        startIcon={<ListAltIcon />}
                        onClick={() => setShowForms(true)}
                    >
                        Formulários
                    </Button>
                )}

                {(groups.isSupervisor || groups.isAdmin) && (
                    <Button
                        variant="contained"
                        startIcon={<TuneIcon />}
                        onClick={async () => {
                            setRequiredFields(board?.required_opportunity_fields || []);
                            setStandardFieldSettings(getStandardFieldSettings(board || selectedJourney));

                            const res = await api.get(
                                `api/enterprises/${enterpriseId}/journeys/${(board || selectedJourney).id}/custom_fields/`
                            );

                            if (res.status === 200) {
                                setJourneyCustomFields(res.data);
                            }

                            setShowRequiredFieldsModal(true);
                        }}
                    >
                        Campos da oportunidade
                    </Button>
                )}

                <ToggleButtonGroup
                    size="small"
                    value={viewMode}
                    exclusive
                    onChange={(e, next) => {
                        if (!next) return;

                        localStorage.setItem(
                            "journey-kanban-board-view",
                            next
                        );
                        setViewMode(next);
                    }}
                >
                    <ToggleButton value="kanban">
                        <ViewKanbanIcon />
                    </ToggleButton>

                    <ToggleButton value="list">
                        <TableRowsIcon />
                    </ToggleButton>

                    <ToggleButton onClick={(event) => setSettingsAnchor(event.currentTarget)}>
                        <SettingsIcon />
                    </ToggleButton>

                    <Menu
                        anchorEl={settingsAnchor}
                        open={Boolean(settingsAnchor)}
                        onClose={() => setSettingsAnchor(null)}
                        PaperProps={{
                            sx: {
                                minWidth: 280,
                            }
                        }}
                    >
                        <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>
                            Campos visíveis no resumo
                        </MenuItem>

                        <Divider />

                        {availableCardFields.map((field) => (
                            <MenuItem
                                dense
                                key={field.key}
                                disabled={field.required}
                                onClick={() => handleVisibleFieldToggle(field)}
                            >
                                <Checkbox
                                    size="small"
                                    checked={field.required || visibleFields[field.key] !== false}
                                    disabled={field.required}
                                    sx={{ mr: 1 }}
                                />

                                <ListItemText
                                    primary={field.label}
                                    secondary={field.required ? "Obrigatório" : null}
                                    primaryTypographyProps={{
                                        fontSize: 14
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Menu>

                </ToggleButtonGroup>

                {viewMode === "list" && (
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => setShowExportEmailDialog(true)}
                    >
                        CSV
                    </Button>
                )}
            </Box>

            {showExportEmailDialog && (
                <Dialog
                    open={showExportEmailDialog}
                    onClose={() => { if (!exportSending) { setShowExportEmailDialog(false); setExportEmail('') } }}
                    fullWidth
                    maxWidth='sm'
                >
                    <DialogTitle>Exportar relatório CSV</DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Typography fontSize="12px" fontWeight="500" color="#21446c" sx={{ mt: 1 }}>
                            O relatório com todas as oportunidades filtradas será enviado por e-mail, para isso, digite-o abaixo.
                        </Typography>
                        <TextField
                            size='small'
                            label="Email"
                            variant="outlined"
                            fullWidth
                            autoFocus
                            sx={{ mt: 2, mb: 2 }}
                            value={exportEmail}
                            onChange={e => setExportEmail(e.target.value)}
                            error={exportEmail.length > 0 && !validateEmail(exportEmail)}
                            helperText={exportEmail && !validateEmail(exportEmail) && "Por favor, insira um e-mail válido"}
                            inputProps={{ maxLength: 100 }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ padding: '0rem 1.25rem 0.75rem 0rem' }}>
                        <Button
                            disabled={!validateEmail(exportEmail) || exportSending}
                            onClick={async () => {
                                setExportSending(true)
                                try {
                                    const params = buildJourneyOpportunityParams({
                                        search,
                                        sellers,
                                        tags: selectedTagIds.filter(tagId => tagId !== NO_TAGS_FILTER),
                                        noTags: selectedTagIds.includes(NO_TAGS_FILTER),
                                        statuses: selectedStatuses,
                                        customFieldFilters: activeCustomFieldFilters,
                                    })

                                    const res = await api.post(
                                        `api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/opportunities/export/?${params.toString()}`,
                                        { email: exportEmail }
                                    )

                                    if (res.status === 200) {
                                        useNotification('Sucesso!', 'O relatório será enviado para o e-mail escolhido assim que processado.', 'success')
                                        setShowExportEmailDialog(false)
                                        setExportEmail('')
                                        return
                                    }
                                    throw res
                                } catch (err) {
                                    console.error(err)
                                    useNotification('Ops!', 'Não foi possível agendar o envio do relatório.', 'danger')
                                } finally {
                                    setExportSending(false)
                                }
                            }}
                        >
                            Enviar
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    )
}

function MoveColumnDialog({ journeyId, onClose, onSubmit }) {
    const [journeys, setJourneys] = useState([])
    const [selectedJourney, setSelectedJourney] = useState("")
    const [selectedPosition, setSelectedPosition] = useState(0)

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const loadJourneys = async () => {
            try {
                const enterpriseId = getSession().profile.enterprise.id

                // AdvancedFilterAndSort's asc/desc are inverted (backend bug); __desc actually sorts ascending
                const res = await api.get(
                    `api/enterprises/${enterpriseId}/journeys/?sort=name__desc`
                )

                if (res.status === 200) {
                    const list = res.data.results || res.data

                    setJourneys(
                        (Array.isArray(list) ? list : []).filter(
                            journey => journey.id !== journeyId
                        )
                    )
                }
            } catch (err) {
                console.error(err)
                useNotification(
                    "Ops",
                    "Não foi possível carregar as jornadas.",
                    "danger"
                )
            }
        }

        loadJourneys()
    }, [])

    const selectedJourneyData = journeys.find(
        j => j.id === selectedJourney
    )

    const positions = Array.from(
        {
            length:
                (selectedJourneyData?.columns?.length || 0) + 1
        },
        (_, i) => i
    )

    const handleJourneyChange = (event) => {
        setSelectedJourney(event.target.value)
        setSelectedPosition(0)
    }

    const handleSubmit = async () => {
        setLoading(true)

        try {
            await onSubmit(selectedJourney, selectedPosition)

            onClose()
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Mover coluna</DialogTitle>

            <DialogContent>
                <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
                    <InputLabel>Jornada</InputLabel>

                    <Select
                        value={selectedJourney}
                        label="Jornada"
                        onChange={handleJourneyChange}
                    >
                        {journeys.map(journey => (
                            <MenuItem
                                key={journey.id}
                                value={journey.id}
                            >
                                {journey.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl
                    fullWidth
                    size="small"
                    disabled={!selectedJourney}
                >
                    <InputLabel>Posição</InputLabel>

                    <Select
                        value={selectedPosition}
                        label="Posição"
                        onChange={(e) =>
                            setSelectedPosition(Number(e.target.value))
                        }
                    >
                        {positions.map(position => (
                            <MenuItem
                                key={position}
                                value={position}
                            >
                                {position}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Cancelar
                </Button>

                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedJourney || loading}
                >
                    {loading ? "Movendo..." : "Mover"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function MoveCardsDialog({ columns, onClose, onSubmit }) {
    const [selectedColumn, setSelectedColumn] = useState("")
    const [loading, setLoading] = useState(false)

    const handleSubmit = async () => {
        if (!selectedColumn || loading) return

        setLoading(true)
        try {
            const completed = await onSubmit(selectedColumn)
            if (!completed) setLoading(false)
        } catch (error) {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setSelectedColumn("")
        onClose()
    }

    return (
        <Dialog
            open={true}
            onClose={() => {
                if (!loading) handleClose()
            }}
            disableEscapeKeyDown={loading}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>Mover oportunidades</DialogTitle>
            <DialogContent>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel>Coluna de destino</InputLabel>
                    <Select
                        value={selectedColumn}
                        label="Coluna de destino"
                        onChange={(e) => setSelectedColumn(e.target.value)}
                    >
                        {columns?.map((col) => (
                            <MenuItem key={col.id} value={col.id}>
                                {col.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedColumn || loading}
                >
                    {loading ? "Processando..." : "Mover"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const listColumns = async (setEdit, setToRemoveOpportunity, board, history) => {
    let columns = [
        { field: "coluna", headerName: "Coluna", flex: 1 },
        { field: "title", headerName: "Título", flex: 1 },
        { field: "value", headerName: "Valor agregado", flex: 1, valueFormatter: (params) => (params.value) ? formatCurrency(params.value) : "" },
        { field: "created_at", headerName: "Criada em", flex: 1, valueFormatter: (params) => (params.value) ? dayjs.utc(params.value).local().format('DD/MM/YYYY HH:mm') : "" },
        { field: "estimated_at", headerName: "Estimada para", flex: 1, valueFormatter: (params) => (params.value) ? dayjs.utc(params.value).local().format('DD/MM/YYYY HH:mm') : "" },
        { field: "closed_at", headerName: "Fechada em", flex: 1, valueFormatter: (params) => (params.value) ? dayjs.utc(params.value).local().format('DD/MM/YYYY HH:mm') : "" },
        {
            field: "updated_at", headerName: "Atualizada em", flex: 1,
            valueGetter: (params) => {
                const updatedAt = params.row.updated_at ? dayjs.utc(params.row.updated_at) : null
                const lastCommentAt = params.row.last_comment_at ? dayjs.utc(params.row.last_comment_at) : null
                if (lastCommentAt && (!updatedAt || lastCommentAt.isAfter(updatedAt))) {
                    return params.row.last_comment_at
                }
                return params.row.updated_at
            },
            valueFormatter: (params) => (params.value) ? dayjs.utc(params.value).local().format('DD/MM/YYYY HH:mm') : ""
        },
        {
            field: "last_comment_at", headerName: "Último comentário", flex: 1,
            valueFormatter: (params) => (params.value) ? dayjs.utc(params.value).local().format('DD/MM/YYYY HH:mm') : ""
        },
        {
            field: "tags", headerName: "Marcadores", flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box display="flex" gap={1}>
                        {Array.isArray(params.row.tags) && params.row.tags.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    padding: ".15rem .25rem",
                                    borderRadius: ".25rem",
                                    fontSize: ".8em",
                                    backgroundColor: item.color,
                                }}
                            >
                                {item.name}
                            </Box>
                        ))}
                    </Box>
                )
            }
        },
        { field: "contact", headerName: "Contato", flex: 1 },
        { field: "owner_name", headerName: "Vendedor", flex: 1 },
        {
            field: "description",
            headerName: "Descrição",
            flex: 1,
            renderCell: ({ value }) => (
                <Box className="journey-list-description-markdown">
                    <KwikMarkdown>{value || ""}</KwikMarkdown>
                </Box>
            )
        },
    ];

    const enterpriseId = getSession().profile.enterprise.id;    
    const custom_fields = await api.get(`api/enterprises/${enterpriseId}/journeys/${board.id}/custom_fields/`);
    if (custom_fields.status === 200) {
        custom_fields.data.forEach(field => {
            columns.push({
                field: `custom_field_${field.id}`,
                headerName: field.name,
                flex: 1,
                valueFormatter: (param) => {
                    if (field.field_type === "date") {
                        return param.value ? dayjs.utc(param.value).local().format('DD/MM/YYYY') : "";
                    } else if (field.field_type === "select") {
                        const option = field.options.find(opt => opt.id == param.value || opt.value == param.value);
                        return option ? option.label : "";
                    } else if (isBooleanCustomField(field)) {
                        return isTruthyCustomFieldValue(param.value) ? "Sim" : "Não";
                    }
                    return param.value || "";
                }
            });
        });
    }

    columns.push({
        field: "actions", headerName: "Ações", flex: .5,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Box>
                <IconButton sx={{ padding: ".25rem" }}><EditIcon sx={{ fontSize: "1.25rem" }} onClick={() => {
                    setEdit({ card: params.row, column: params.row.column })
                    const urlParams = new URLSearchParams(location.search)
                    urlParams.set("selectedCard", params.row.id)
                    history.push({
                        pathname: location.pathname,
                        search: urlParams.toString(),
                    })
                }} /></IconButton>
                <IconButton sx={{ padding: ".25rem" }}><DeleteIcon sx={{ fontSize: "1.25rem", color: "#c95f50" }} onClick={() => setToRemoveOpportunity({
                    data: params.row,
                    column: params.row.column
                })} /></IconButton>
            </Box>
        )
    });


    return columns;
}

const CustomToolbar = () => {
    return (
        <GridToolbarContainer className={gridClasses.toolbarContainer} >
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
        </GridToolbarContainer>
    )
}

const convertOpportunityListRows = (opportunities, board) => {
    const columnsById = (board?.columns || []).reduce((acc, column) => {
        acc[column.id] = column.title
        return acc
    }, {})

    return (opportunities || []).map(opportunity => {
        const customFields = {}

        if (Array.isArray(opportunity.custom_fields)) {
            opportunity.custom_fields.forEach(field => {
                customFields[`custom_field_${field.field}`] = field.value
            })
        }

        return {
            ...opportunity,
            ...customFields,
            coluna: opportunity.column_name || columnsById[opportunity.column] || "",
        }
    })
}

const Kanban = ({ selectedJourney, close, allUsers }) => {

    const [board, setBoard] = useState(selectedJourney || { columns: [] })
    const [listColumnsState, setListColumnsState] = useState([])
    const [showRequiredFieldsModal, setShowRequiredFieldsModal] = useState(false)
    const [requiredFields, setRequiredFields] = useState([])
    const [journeyCustomFields, setJourneyCustomFields] = useState([])
    const [standardFieldSettings, setStandardFieldSettings] = useState(getStandardFieldSettings(selectedJourney))
    const cardFieldsStorageKey = useMemo(() => getCardFieldsStorageKey(board || selectedJourney), [board?.id, selectedJourney?.id])
    const availableCardFields = useMemo(() => {
        const journey = board || selectedJourney
        return [
            ...getAvailableCardSummaryFields(journey),
            ...getAvailableCustomCardSummaryFields(journey),
        ]
    }, [board, selectedJourney])
    const [visibleFields, setVisibleFields] = useState(() => normalizeVisibleFields({}, availableCardFields))

    useEffect(() => {
        let storedFields = {}
        try {
            storedFields = JSON.parse(localStorage.getItem(cardFieldsStorageKey) || "{}")
        } catch (error) {
            storedFields = {}
        }

        const normalized = normalizeVisibleFields(storedFields, availableCardFields)
        setVisibleFields(normalized)
        localStorage.setItem(cardFieldsStorageKey, JSON.stringify(normalized))
    }, [cardFieldsStorageKey, availableCardFields])

    const [moreMenuState, setMoreMenuState] = useState({
      anchor: null,
      columnId: null,
    })

    const history = useHistory()
    const location = useLocation()

    useEffect(() => {
        const loadColumns = async () => {
            if (!board || !board.id) return;
            const cols = await listColumns(setEdit, setToRemoveOpportunity, board, history)
            setListColumnsState(cols)
        }
        loadColumns()
    }, [board?.id])
    const [showImportLeads, setShowImportLeads] = useState(false)
    const [showForms, setShowForms] = useState(false)
    const handleImportLeads = async (contacts) => {
        setShowImportLeads(false);
        if (!contacts || !contacts.length) return;
        const firstColumn = board?.columns?.[0];
        if (!firstColumn) return;
        for (const contact of contacts) {
            await newCard(selectedJourney.id, firstColumn.id, setBoard, () => {}, () => {});
        }
    };
    const [transfer, setTransfer] = useState(null);
    // { mode: "copy"|"move", card }

    const [journeysList, setJourneysList] = useState([]);
    const [targetJourneyId, setTargetJourneyId] = useState(null);
    const [targetJourney, setTargetJourney] = useState(null);
    const [targetColumns, setTargetColumns] = useState([]);
    const [targetColumnId, setTargetColumnId] = useState("");
    const [transferLoading, setTransferLoading] = useState(false);

    const defaultCopyOptions = {
        status: true,
        description: true,
        value_currency: true,
        contact: true,
        contact_enterprise: true,
        owner: true,
        tags: true,
        estimated_at: true,
        secondary_contacts: true,
        custom_fields: true,
        closed_at: true,
        history: true,
    };
    const [copyOptions, setCopyOptions] = useState(defaultCopyOptions);    

    const enterpriseId = getSession().profile.enterprise.id;    

    const openTransfer = async (mode, card) => {
        setTransfer({ mode, card });
        setTargetJourneyId(null);
        setTargetJourney(null);
        setTargetColumns([]);
        setTargetColumnId("");

        if (mode === "copy") setCopyOptions(defaultCopyOptions);

        try {
            const res = await api.get(`api/enterprises/${enterpriseId}/journeys/?sort=name__desc`);
            if (res.status === 200) {
                const list = res.data.results || res.data;
                setJourneysList(Array.isArray(list) ? list : []);
            } else {
                useNotification("Ops", "Não foi possível carregar as jornadas.", "danger");
            }
        } catch (e) {
            useNotification("Ops", "Não foi possível carregar as jornadas.", "danger");
        }
    };

    const closeTransfer = () => {
        setTransfer(null);
        setTargetJourneyId(null);
        setTargetJourney(null);
        setTargetColumns([]);
        setTargetColumnId("");
        setTransferLoading(false);
    };


    const handleOpportunitiesCreated = async (columnId, opportunitiesData) => {
        // Se recebeu as oportunidades do POST, adiciona direto sem fazer GET
        if (opportunitiesData && opportunitiesData.length > 0) {
            setBoard(prev => {
                const updatedBoard = { ...prev };
                const columnIndex = updatedBoard.columns.findIndex(c => c.id === columnId);
                
                if (columnIndex !== -1) {
                    // Adiciona as novas oportunidades ao final da coluna
                    const activeOpportunities = opportunitiesData.filter(item => !item.archived)
                    const activeValue = activeOpportunities
                        .reduce((acc, item) => acc + Number(item.value || 0), 0)
                    updatedBoard.columns[columnIndex].cards.push(...opportunitiesData);
                    updatedBoard.columns[columnIndex].opportunity_count = Number(updatedBoard.columns[columnIndex].opportunity_count || 0) + activeOpportunities.length
                    updatedBoard.columns[columnIndex].total_value = Number(updatedBoard.columns[columnIndex].total_value || 0) + activeValue
                }
                
                return updatedBoard;
            });
            console.log('[Kanban] Oportunidades adicionadas com sucesso');
        }
    };
    const [viewMode, setViewMode] = useState(localStorage.getItem('journey-kanban-board-view') || "kanban")
    const [listRows, setListRows] = useState([])
    const [listRowCount, setListRowCount] = useState(0)
    const [listPage, setListPage] = useState(0)
    const [listPageSize, setListPageSize] = useState(JOURNEY_LIST_PAGE_SIZE)
    const [listSortModel, setListSortModel] = useState([])
    const [listLoading, setListLoading] = useState(false)
    const [listRefreshKey, setListRefreshKey] = useState(0)
    const [sellers, setSellers] = useState([])
    const [selectedTags, setSelectedTags] = useState([])
    const [selectedStatuses, setSelectedStatuses] = useState([])
    const [customFieldFilters, setCustomFieldFilters] = useState([])
    const [edit, setEdit] = useState()
    const [loading, setLoading] = useState(false)
    const [toRemove, setToRemove] = useState(null)
    const [toMoveCards, setToMoveCards] = useState(null)
    const [toMoveColumn, setToMoveColumn] = useState(null)
    const [toRemoveOpportunity, setToRemoveOpportunity] = useState(null)
    const [showAutomations, setShowAutomations] = useState(null)
    const [isBoardDragging, setIsBoardDragging] = useState(false)
    const boardScrollRef = useRef(null)
    const boardDragScroll = useRef({
        active: false,
        startX: 0,
        scrollLeft: 0,
    })
    const cardDragAutoScroll = useRef({
        active: false,
        moved: false,
        startX: 0,
        startY: 0,
        pointerX: null,
        pointerY: null,
        frame: null,
    })
    const dropTargetHighlightRef = useRef({ cardElement: null })
    const columnPaginationRef = useRef({})
    const loadingColumnIdsRef = useRef(new Set())
    const selectedCardLoadRef = useRef(null)
    const listRequestRef = useRef(0)
    const [pendingJourneyUpdate, setPendingJourneyUpdate] = useState(false)
    const editRef = useRef(null)
    const boardRefreshTimeoutRef = useRef(null)
    const boardRefreshInFlightRef = useRef(false)
    const boardRefreshQueuedJourneyRef = useRef(null)
    const boardRefreshFailureCountRef = useRef(0)
    const boardRefreshWarningShownRef = useRef(false)

    const [search, setSearch] = useState("")
    const [debounced, setDebounced] = useState("")

    const selectedTagIds = useMemo(() => selectedTags.map(String), [selectedTags])
    const selectedSellerIds = useMemo(() => sellers.map(String), [sellers])
    const selectedJourneyTagIds = useMemo(
        () => selectedTagIds.filter(tagId => tagId !== NO_TAGS_FILTER),
        [selectedTagIds]
    )
    const includeNoTags = selectedTagIds.includes(NO_TAGS_FILTER)
    const customFieldFilterOptions = useMemo(() => getCustomFieldFilterOptions(board || selectedJourney), [board, selectedJourney])
    const activeCustomFieldFilters = useMemo(
        () => normalizeCustomFieldFilters(customFieldFilters, customFieldFilterOptions),
        [customFieldFilters, customFieldFilterOptions]
    )
    const sellersFilterKey = useMemo(() => sellers.join(","), [sellers])
    const tagsFilterKey = useMemo(() => selectedJourneyTagIds.join(","), [selectedJourneyTagIds])
    const noTagsFilterKey = includeNoTags ? "true" : "false"
    const statusesFilterKey = useMemo(() => selectedStatuses.join(","), [selectedStatuses])
    const customFieldFiltersKey = useMemo(() => JSON.stringify(activeCustomFieldFilters), [activeCustomFieldFilters])
    const listSellers = useMemo(
        () => sellersFilterKey ? sellersFilterKey.split(",").filter(Boolean) : [],
        [sellersFilterKey]
    )
    const listTags = useMemo(
        () => tagsFilterKey ? tagsFilterKey.split(",").filter(Boolean) : [],
        [tagsFilterKey]
    )
    const listStatuses = useMemo(
        () => statusesFilterKey ? statusesFilterKey.split(",").filter(Boolean) : [],
        [statusesFilterKey]
    )
    const boardFilterOptions = useMemo(() => ({
        search: debounced,
        sellers: listSellers,
        tags: listTags,
        noTags: includeNoTags,
        statuses: listStatuses,
        customFieldFilters: activeCustomFieldFilters,
    }), [customFieldFiltersKey, debounced, includeNoTags, listSellers, listStatuses, listTags])

    const groups = UserPermissions()
    const enterprise = getSession()?.profile?.enterprise

    useInjectComponent(
        <NewOpportunityButton
            buttonClicked={(columnId) =>
                newCard(
                    selectedJourney.id,
                    columnId,
                    setBoard,
                    setEdit,
                    () => removeColumnCallback(columnId, setBoard)
                )
            }
        />,
        "react-kanban-column",
        [board, viewMode]
    )

    useEffect(() => {
        columnPaginationRef.current = (board?.columns || []).reduce((acc, column) => {
            const loadedCount = (column.cards || []).length
            acc[column.id] = {
                hasMore: Boolean(column.cards_has_more),
                nextOffset: Number(column.cards_next_offset ?? loadedCount),
                loadedCount,
            }
            return acc
        }, {})
    }, [board])

    const loadMoreColumn = useCallback(async (columnId) => {
        const numericColumnId = Number(columnId || 0)
        if (!numericColumnId || !board?.id) return

        const pagination = columnPaginationRef.current[numericColumnId]
        if (!pagination?.hasMore || loadingColumnIdsRef.current.has(numericColumnId)) return

        loadingColumnIdsRef.current.add(numericColumnId)

        try {
            const data = await loadColumnCards(
                board,
                numericColumnId,
                pagination.nextOffset,
                JOURNEY_COLUMN_PAGE_SIZE,
                boardFilterOptions
            )

            if (!data) {
                useNotification("Ops", "Não foi possível carregar mais oportunidades.", "danger")
                return
            }

            const nextCards = Array.isArray(data.results) ? data.results : []
            setBoard(prev => ({
                ...prev,
                columns: (prev.columns || []).map(column => {
                    if (Number(column.id) !== numericColumnId) return column

                    const existingIds = new Set((column.cards || []).map(card => card.id))
                    const mergedCards = [
                        ...(column.cards || []),
                        ...nextCards.filter(card => !existingIds.has(card.id)),
                    ]

                    return {
                        ...column,
                        cards: mergedCards,
                        cards_has_more: Boolean(data.has_more),
                        cards_next_offset: data.next,
                        cards_limit: data.limit ?? column.cards_limit,
                        opportunity_count: data.count ?? column.opportunity_count,
                        total_value: data.total_value ?? column.total_value,
                    }
                })
            }))
        } finally {
            loadingColumnIdsRef.current.delete(numericColumnId)
        }
    }, [board, boardFilterOptions])

    const handleBoardDragScrollStart = (event) => {
        const container = boardScrollRef.current
        if (!canStartBoardDragScroll(event, container)) return

        event.preventDefault()
        boardDragScroll.current = {
            active: true,
            startX: event.clientX,
            scrollLeft: container.scrollLeft,
        }
        setIsBoardDragging(true)
    }

    const handleCardDragAutoScrollStart = (event) => {
        const container = boardScrollRef.current
        if (!canStartCardDragAutoScroll(event, container)) return

        container.classList.add("card-dragging")
        cardDragAutoScroll.current = {
            ...cardDragAutoScroll.current,
            moved: false,
            startX: event.clientX,
            startY: event.clientY,
            pointerX: event.clientX,
        }
    }

    const closeOpportunity = () => {
        setEdit(null)
        const params = new URLSearchParams(location.search)
        params.delete("selectedCard")
        history.push({
            pathname: location.pathname,
            search: params.toString(),
        })

        if (viewMode === "list") {
            setListRefreshKey(prev => prev + 1)
        }
    }

    const deleteCard = (data) => {
        if (viewMode === "list" && data?.id) {
            setListRows(prev => prev.filter(row => row.id !== data.id))
            setListRowCount(prev => Math.max(0, Number(prev || 0) - 1))
        }

        closeOpportunity()
    }

    useEffect(() => {
        if (viewMode !== "kanban") return
        loadKanban(selectedJourney, setLoading, setBoard, setEdit, close, boardFilterOptions)
    }, [selectedJourney?.id, viewMode, boardFilterOptions])

    useEffect(() => {
        if (!isBoardDragging) return

        const handleMouseMove = (event) => {
            if (!boardDragScroll.current.active || !boardScrollRef.current) return

            event.preventDefault()
            const dragDistance = event.clientX - boardDragScroll.current.startX
            boardScrollRef.current.scrollLeft = boardDragScroll.current.scrollLeft - dragDistance
        }

        const handleMouseUp = () => {
            boardDragScroll.current.active = false
            setIsBoardDragging(false)
        }

        document.addEventListener("mousemove", handleMouseMove)
        document.addEventListener("mouseup", handleMouseUp)

        return () => {
            document.removeEventListener("mousemove", handleMouseMove)
            document.removeEventListener("mouseup", handleMouseUp)
        }
    }, [isBoardDragging])

    useEffect(() => {
        const handleMouseMove = (event) => {
            cardDragAutoScroll.current.pointerX = event.clientX
            cardDragAutoScroll.current.pointerY = event.clientY

            const deltaX = event.clientX - cardDragAutoScroll.current.startX
            const deltaY = event.clientY - cardDragAutoScroll.current.startY
            if (Math.hypot(deltaX, deltaY) > 5) {
                cardDragAutoScroll.current.moved = true
            }
        }

        const handleTouchMove = (event) => {
            const touch = event.touches?.[0]
            if (!touch) return

            cardDragAutoScroll.current.pointerX = touch.clientX
            cardDragAutoScroll.current.pointerY = touch.clientY
        }

        const stopAutoScroll = () => {
            stopCardDragAutoScroll(cardDragAutoScroll)
            const container = boardScrollRef.current
            window.requestAnimationFrame(() => container?.classList.remove("card-dragging"))
        }

        window.addEventListener("mousemove", handleMouseMove, true)
        window.addEventListener("touchmove", handleTouchMove, { capture: true, passive: true })
        window.addEventListener("mouseup", stopAutoScroll, true)
        window.addEventListener("touchend", stopAutoScroll, true)
        window.addEventListener("touchcancel", stopAutoScroll, true)

        return () => {
            window.removeEventListener("mousemove", handleMouseMove, true)
            window.removeEventListener("touchmove", handleTouchMove, { capture: true, passive: true })
            window.removeEventListener("mouseup", stopAutoScroll, true)
            window.removeEventListener("touchend", stopAutoScroll, true)
            window.removeEventListener("touchcancel", stopAutoScroll, true)
            stopCardDragAutoScroll(cardDragAutoScroll)
        }
    }, [cardDragAutoScroll])

    useEffect(() => {
        if (viewMode !== "kanban" || loading || edit) return

        const boardElement = boardScrollRef.current
        if (!boardElement) return

        const scrollChecks = []
        const cleanups = Array.from(boardElement.getElementsByClassName("react-kanban-column")).flatMap(columnElement => {
            const columnId = getColumnIdFromColumnElement(columnElement)
            const scrollElement = getColumnCardsScrollElement(columnElement)

            if (!columnId || !scrollElement) return []

            const handleScroll = () => {
                const distanceFromBottom = scrollElement.scrollHeight - (scrollElement.scrollTop + scrollElement.clientHeight)

                if (distanceFromBottom <= COLUMN_SCROLL_BOTTOM_THRESHOLD) {
                    loadMoreColumn(columnId)
                }
            }

            scrollChecks.push(handleScroll)
            scrollElement.addEventListener("scroll", handleScroll)
            return [() => scrollElement.removeEventListener("scroll", handleScroll)]
        })

        let scrollCheckFrame = null
        const scheduleScrollCheck = () => {
            if (scrollCheckFrame) window.cancelAnimationFrame(scrollCheckFrame)
            scrollCheckFrame = window.requestAnimationFrame(() => {
                scrollCheckFrame = null
                scrollChecks.forEach(check => check())
            })
        }

        scheduleScrollCheck()
        window.addEventListener("resize", scheduleScrollCheck)

        return () => {
            if (scrollCheckFrame) window.cancelAnimationFrame(scrollCheckFrame)
            window.removeEventListener("resize", scheduleScrollCheck)
            cleanups.forEach(cleanup => cleanup())
        }
    }, [board?.columns, edit, loadMoreColumn, loading, viewMode])

    useEffect(() => {
        editRef.current = edit
    }, [edit])

    const modifiedBoard = useMemo(() => {
        const search = debounced?.toLowerCase();

        if (!board || !Array.isArray(board.columns)) {
            return { columns: [] };
        }

        return {
            columns: board.columns.map(column => ({
                ...column,
                cards: (column.cards || []).filter(card => {
                    const matchesSeller = selectedSellerIds.length === 0 || selectedSellerIds.includes(String(card.owner_id))
                    const cardTags = card.tags || []
                    const matchesNoTags = includeNoTags && cardTags.length === 0
                    const matchesSelectedTags = selectedJourneyTagIds.length > 0 && cardTags.some(tag => selectedJourneyTagIds.includes(String(tag.id)))
                    const matchesTags = selectedTagIds.length === 0 || matchesNoTags || matchesSelectedTags
                    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(card.status)
                    const matchesCustomFields = matchesCustomFieldFilters(card, activeCustomFieldFilters, customFieldFilterOptions)
                    if (!search && card.archived) {
                        return false;
                    }
                    const matchesSearch = !search || String(card.title || "").toLowerCase().includes(search)
                    return matchesSeller && matchesTags && matchesStatus && matchesCustomFields && matchesSearch
                })
            }))
        }
    }, [board, selectedSellerIds, selectedTagIds, selectedJourneyTagIds, includeNoTags, selectedStatuses, activeCustomFieldFilters, customFieldFilterOptions, debounced])

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebounced(search)
        }, 1000)

        return () => {
            clearTimeout(handler)
        }
    }, [search])

    useEffect(() => {
        setListPage(0)
    }, [board?.id, debounced, sellersFilterKey, tagsFilterKey, noTagsFilterKey, statusesFilterKey, customFieldFiltersKey])

    const loadListRows = useCallback(async () => {
        if (viewMode !== "list" || !board?.id) return

        const requestId = listRequestRef.current + 1
        listRequestRef.current = requestId
        setListLoading(true)

        try {
            const data = await loadJourneyOpportunities(board, {
                limit: listPageSize,
                offset: listPage * listPageSize,
                sortModel: listSortModel,
                search: debounced,
                sellers: listSellers,
                tags: listTags,
                noTags: includeNoTags,
                statuses: listStatuses,
                customFieldFilters: activeCustomFieldFilters,
            })

            if (listRequestRef.current !== requestId) return

            if (!data) {
                setListRows([])
                setListRowCount(0)
                useNotification("Ops", "Não foi possível carregar as oportunidades.", "danger")
                return
            }

            setListRows(convertOpportunityListRows(data.results || [], board))
            setListRowCount(Number(data.count || 0))
        } finally {
            if (listRequestRef.current === requestId) {
                setListLoading(false)
            }
        }
    }, [activeCustomFieldFilters, board, debounced, includeNoTags, listPage, listPageSize, listRefreshKey, listSellers, listSortModel, listStatuses, listTags, viewMode])

    useEffect(() => {
        loadListRows()
    }, [loadListRows])

    useEffect(() => {
        const loadTargetJourney = async () => {
            if (!targetJourneyId) return;

            setTransferLoading(true);
            try {
                const res = await api.get(`api/enterprises/${enterpriseId}/journeys/${targetJourneyId}/?cards_limit=0`);
                if (res.status === 200) {
                    setTargetJourney(res.data);
                    setTargetColumns(res.data.columns || []);
                    setTargetColumnId("");
                } else {
                    useNotification("Ops", "Jornada de destino não encontrada.", "danger");
                }
            } finally {
                setTransferLoading(false);
            }
        };

        loadTargetJourney();
    }, [targetJourneyId]);


    const handleConfirmTransfer = async () => {
        if (!transfer?.card?.id || !targetJourneyId || !targetColumnId) return;

        const cardId = transfer.card.id;
        const sourceJourneyId = selectedJourney.id;
        const isCopy = transfer.mode === "copy";
        const optionsToUse = isCopy ? copyOptions : null; // move não usa seleção

        setTransferLoading(true);

        try {
            const transferPayload = {
                mode: isCopy ? "copy" : "move",
                target_journey_id: targetJourneyId,
                target_column_id: targetColumnId,
            };
        
            // COPY: manda seleção do modal
            if (isCopy) {
                transferPayload.copy_options = {
                    status: !!optionsToUse.status,
                    description: !!optionsToUse.description,
                    value_currency: !!optionsToUse.value_currency,
                    contact: !!optionsToUse.contact,
                    contact_enterprise: !!optionsToUse.contact_enterprise,
                    owner: !!optionsToUse.owner,
                    tags: !!optionsToUse.tags,
                    estimated_at: !!optionsToUse.estimated_at,
                    secondary_contacts: !!optionsToUse.secondary_contacts,
                    custom_fields: !!optionsToUse.custom_fields,
                    closed_at: !!optionsToUse.closed_at,
                };
                // history separado (requisito)
                transferPayload.copy_history = optionsToUse.history !== false; // default true
            } else {
                // MOVE: sempre copia history + custom fields no backend (e ignora inexistentes)
                transferPayload.copy_history = true;
            }
        
            const res = await api.post(
                `api/enterprises/${enterpriseId}/journeys/${sourceJourneyId}/opportunities/${cardId}/transfer/`,
                transferPayload
            );
        
            if (![200, 201].includes(res.status)) {
                useNotification("Ops", "Algo deu errado ao transferir a oportunidade.", "danger");
                return;
            }
        
            // Atualiza UI da jornada atual:
            // - copy: mantém original e adiciona novo card? (como estamos na jornada origem, não precisa mexer)
            // - move: remove o card da jornada atual (origem)
            if (!isCopy) {
                setBoard(prev => ({
                    ...prev,
                    columns: prev.columns.map(col => {
                        const removedCard = (col.cards || []).find(c => c.id === cardId)
                        const valueDelta = removedCard?.archived ? 0 : Number(removedCard?.value || 0)

                        return {
                        ...col,
                            cards: (col.cards || []).filter(c => c.id !== cardId),
                            opportunity_count: removedCard && !removedCard.archived ? Math.max(0, Number(col.opportunity_count ?? col.cards?.length ?? 0) - 1) : col.opportunity_count,
                            total_value: removedCard ? Math.max(0, Number(col.total_value || 0) - valueDelta) : col.total_value,
                        }
                    })
                }));

                if (viewMode === "list") {
                    setListRows(prev => prev.filter(row => row.id !== cardId))
                    setListRowCount(prev => Math.max(0, Number(prev || 0) - 1))
                }
            }

            useNotification("Sucesso", isCopy ? "Oportunidade copiada com sucesso." : "Oportunidade movida com sucesso.", "success");
            closeTransfer();
        } catch (e) {
            useNotification("Ops", "Algo deu errado ao transferir a oportunidade.", "danger");
        } finally {
            setTransferLoading(false);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const selectedCard = params.get("selectedCard")

        if (!selectedCard) {
            selectedCardLoadRef.current = null
            return
        }

        const allCards = modifiedBoard?.columns?.flatMap(c => c.cards || [])
        const filtered = allCards?.find(card => card.id === Number(selectedCard))

        if (!filtered) {
            if (!board?.id || selectedCardLoadRef.current === selectedCard) return

            selectedCardLoadRef.current = selectedCard
            const loadSelectedCard = async () => {
                let loaded = false
                try {
                    const response = await api.get(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/opportunities/${selectedCard}/`)
                    if (response.status === 200) {
                        loaded = true
                        setEdit(prev => {
                            if (prev?.card?.id === response.data.id) return prev
                            return { card: response.data, column: response.data.column }
                        })
                        return
                    }
                } catch (error) {
                    loaded = false
                } finally {
                    if (!loaded) {
                        selectedCardLoadRef.current = null
                    }
                }
            }

            loadSelectedCard()
            return
        }

        setEdit(prev => {
            if (prev?.card?.id === filtered.id) return prev
            selectedCardLoadRef.current = null
            return { card: filtered, column: 0 }
        })
    }, [board?.id, enterpriseId, location.search, modifiedBoard, selectedJourney.id])

    const requestBoard = async (journeyId) => {
        const enterpriseId = getSession().profile.enterprise.id
        const params = buildJourneyOpportunityParams({
            ...boardFilterOptions,
            limit: undefined,
            offset: undefined,
        })
        params.set("cards_limit", JOURNEY_COLUMN_PAGE_SIZE)
        params.set("cards_offset", 0)
        return api.get(
            `api/enterprises/${enterpriseId}/journeys/${journeyId}/?${params.toString()}`,
            {
                timeout: 60000,
            }
        )
    }

    const applyBoardResponse = (response) => {
        if (response?.status !== 200) return false

        setBoard(response.data)

        if (viewMode === "list") {
            setListRefreshKey(prev => prev + 1)
        }

        boardRefreshFailureCountRef.current = 0
        boardRefreshWarningShownRef.current = false
        return true
    }

    const refreshBoard = async (journeyId) => {
        if (!journeyId) return false

        if (boardRefreshInFlightRef.current) {
            boardRefreshQueuedJourneyRef.current = journeyId
            return false
        }

        boardRefreshInFlightRef.current = true
        let refreshSucceeded = false

        try {
            refreshSucceeded = applyBoardResponse(
                await requestBoard(journeyId)
            )
        } catch (error) {
            refreshSucceeded = false
        } finally {
            boardRefreshInFlightRef.current = false

            const queuedJourneyId = boardRefreshQueuedJourneyRef.current
            boardRefreshQueuedJourneyRef.current = null

            if (queuedJourneyId) {
                scheduleBoardRefresh(queuedJourneyId, 5000)
            } else if (!refreshSucceeded) {
                boardRefreshFailureCountRef.current += 1

                if (
                    boardRefreshFailureCountRef.current >= 3 &&
                    !boardRefreshWarningShownRef.current
                ) {
                    boardRefreshWarningShownRef.current = true
                    useNotification(
                        "Atenção!",
                        "A jornada ainda está sendo processada. A tela será atualizada automaticamente.",
                        "warning"
                    )
                }

                scheduleBoardRefresh(
                    journeyId,
                    Math.min(
                        5000 * (2 ** (boardRefreshFailureCountRef.current - 1)),
                        30000
                    )
                )
            }
        }

        return refreshSucceeded
    }

    const refreshBoardAfterBulkMove = async (journeyId) => {
        const deadline = Date.now() + 60000

        while (boardRefreshInFlightRef.current) {
            if (Date.now() >= deadline) {
                throw new Error("Timed out waiting for the current board refresh")
            }

            await new Promise(resolve => setTimeout(resolve, 100))
        }

        if (boardRefreshTimeoutRef.current) {
            clearTimeout(boardRefreshTimeoutRef.current)
            boardRefreshTimeoutRef.current = null
        }

        boardRefreshQueuedJourneyRef.current = null
        boardRefreshInFlightRef.current = true

        try {
            const refreshed = applyBoardResponse(
                await requestBoard(journeyId)
            )

            if (!refreshed) {
                throw new Error("Board refresh failed after bulk move")
            }
        } catch (error) {
            useNotification(
                "Ops",
                "As oportunidades foram movidas, mas não foi possível atualizar a jornada.",
                "danger"
            )
            throw error
        } finally {
            boardRefreshInFlightRef.current = false

            const queuedJourneyId = boardRefreshQueuedJourneyRef.current
            boardRefreshQueuedJourneyRef.current = null

            if (queuedJourneyId) {
                scheduleBoardRefresh(queuedJourneyId, 5000)
            }
        }
    }

    const scheduleBoardRefresh = (journeyId, delay = 1000) => {
        if (!journeyId) return

        if (boardRefreshTimeoutRef.current) {
            clearTimeout(boardRefreshTimeoutRef.current)
        }

        boardRefreshTimeoutRef.current = setTimeout(() => {
            boardRefreshTimeoutRef.current = null
            refreshBoard(journeyId)
        }, delay)
    }

    useEffect(() => {
        if (!selectedJourney?.id || !enterpriseId) return

        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const socket = new WebSocket(`${protocol}://${window.location.host}/chat/ws/journey/${enterpriseId}/${selectedJourney.id}/`)

        socket.onmessage = (event) => {
            let data = {}
            try {
                data = JSON.parse(event.data)
            } catch (e) {
                return
            }

            if (data.event !== 'journey.updated' && data.message !== 'UPDATED') return

            if (editRef.current) {
                setPendingJourneyUpdate(true)
                return
            }

            scheduleBoardRefresh(
                selectedJourney.id,
                data.message === 'BULK_UPDATED' ? 5000 : 1000
            )
        }

        return () => {
            socket.close()
            if (boardRefreshTimeoutRef.current) {
                clearTimeout(boardRefreshTimeoutRef.current)
                boardRefreshTimeoutRef.current = null
            }
            boardRefreshQueuedJourneyRef.current = null
            boardRefreshFailureCountRef.current = 0
            boardRefreshWarningShownRef.current = false
        }
    }, [selectedJourney?.id, enterpriseId])

    useEffect(() => {
        if (!pendingJourneyUpdate || edit) return

        setPendingJourneyUpdate(false)
        scheduleBoardRefresh(selectedJourney.id, 100)
    }, [pendingJourneyUpdate, edit, selectedJourney?.id])

    useEffect(() => () => {
        if (boardRefreshTimeoutRef.current)
            clearTimeout(boardRefreshTimeoutRef.current)
    }, [])

    const params = new URLSearchParams(location.search)
    const latestChanged = params.get("latestChanged")

    return (
        <JourneyContext.Provider value={{board}}>
            <Box className='main-journay-wrapper'>
                <Dialog
                    open={showImportLeads}
                    maxWidth="sm" 
                    fullWidth
                    disableEscapeKeyDown
                    onBackdropClick={(event) => event.preventDefault()}
                >
                    <ImportLeadsModal 
                        onClose={() => setShowImportLeads(false)}
                        selectedJourney={selectedJourney}
                        firstColumn={board?.columns?.[0]}
                        setBoard={setBoard}
                        setEdit={setEdit}
                        onOpportunitiesCreated={handleOpportunitiesCreated}
                    />
                </Dialog>
                <Loading loading={loading} />

                {showForms && 
                    <WebForms journey={selectedJourney} handleClose={()=>setShowForms(false)}/>
                }
                {showAutomations && board ?
                    <JourneyAutomations data={showAutomations} opportunityCustomFields={board.custom_fields} journey={board} handleClose={(data) => {
                        const column = (board.columns || []).find(column => column.id === showAutomations.id)
                        if (column) {
                            column.automation_count = data.rows.length
                            setBoard({...board})
                        }
                        setShowAutomations(null);
                    }} />
                    : null}
                {edit ?
                    <Opportunity
                        selectedJourney={board}
                        allUsers={allUsers}
                        data={edit}
                        handleClose={closeOpportunity}
                        board={board}
                        setBoard={setBoard}
                        onDelete={(data) => deleteCard(data)}
                        onSave={() => {
                            if (viewMode === "list") {
                                setListRefreshKey(prev => prev + 1)
                            }
                        }}
                    />
                    : null
                }
                
                <KanbanHeader
                    close={close}
                    search={search}
                    setSearch={setSearch}
                    sellers={sellers}
                    setSellers={setSellers}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    selectedStatuses={selectedStatuses}
                    setSelectedStatuses={setSelectedStatuses}
                    customFieldFilters={customFieldFilters}
                    setCustomFieldFilters={setCustomFieldFilters}
                    allUsers={allUsers}
                    selectedJourney={selectedJourney}
                    enterpriseId={enterpriseId}
                    board={board}
                    setRequiredFields={setRequiredFields}
                    setJourneyCustomFields={setJourneyCustomFields}
                    standardFieldSettings={standardFieldSettings}
                    setStandardFieldSettings={setStandardFieldSettings}
                    setShowRequiredFieldsModal={setShowRequiredFieldsModal}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    setShowImportLeads={setShowImportLeads}
                    setShowForms={setShowForms}
                    groups={groups}
                    availableCardFields={availableCardFields}
                    visibleFields={visibleFields}
                    setVisibleFields={setVisibleFields}
                    cardFieldsStorageKey={cardFieldsStorageKey}
                />

                <Dialog open={showRequiredFieldsModal} onClose={() => setShowRequiredFieldsModal(false)} fullWidth maxWidth="md">
                    <DialogTitle>Campos da oportunidade — {selectedJourney.name}</DialogTitle>
                    <DialogContent>
                        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                            Configure os campos padrão da jornada, incluindo nome exibido, exibição no formulário, obrigatoriedade e exibição no resumo do card.
                        </Alert>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Campos padrão
                        </Typography>

                        <Stack spacing={1.5}>
                            {STANDARD_OPPORTUNITY_FIELDS.map((field) => {
                                const currentSettings = standardFieldSettings?.[field.key] || {}
                                const required = field.required || (Array.isArray(requiredFields) && requiredFields.includes(field.key))
                                const hidden = !required && currentSettings.hidden === true
                                const showInSummary = required || currentSettings.show_in_summary !== false

                                return (
                                    <Box
                                        key={field.key}
                                        sx={{
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            borderRadius: 1,
                                            p: 1.25,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Nome exibido
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={currentSettings.label ?? field.label}
                                                placeholder={field.label}
                                                onChange={(event) => {
                                                    setStandardFieldSettings(prev => setStandardFieldSettingValue(prev, field.key, 'label', event.target.value))
                                                }}
                                                inputProps={{ maxLength: 64 }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                            {field.form && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={!hidden}
                                                            disabled={field.canHide === false || required}
                                                            onChange={(event) => {
                                                                const nextHidden = !event.target.checked
                                                                setStandardFieldSettings(prev => setStandardFieldSettingValue(prev, field.key, 'hidden', nextHidden))
                                                                if (nextHidden) {
                                                                    setRequiredFields(prev => (Array.isArray(prev) ? prev.filter(item => item !== field.key) : []))
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Exibir no formulário"
                                                />
                                            )}

                                            {field.summary && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={showInSummary}
                                                            disabled={required || hidden}
                                                            onChange={(event) => {
                                                                setStandardFieldSettings(prev => setStandardFieldSettingValue(prev, field.key, 'show_in_summary', event.target.checked))
                                                            }}
                                                        />
                                                    }
                                                    label="Exibir no resumo"
                                                />
                                            )}

                                            {field.form && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={required}
                                                            disabled={field.required || hidden}
                                                            onChange={(event) => {
                                                                setRequiredFields(prev => {
                                                                    const arr = Array.isArray(prev) ? [...prev] : []
                                                                    if (event.target.checked) {
                                                                        if (!arr.includes(field.key)) arr.push(field.key)
                                                                    } else {
                                                                        const idx = arr.indexOf(field.key)
                                                                        if (idx > -1) arr.splice(idx, 1)
                                                                    }
                                                                    return arr
                                                                })
                                                            }}
                                                        />
                                                    }
                                                    label="Obrigatório"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Stack>

                        {journeyCustomFields.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Campos customizados
                                </Typography>

                                <Stack spacing={1}>
                                    {journeyCustomFields.map((cf) => (
                                        <Box
                                            key={cf.id}
                                            sx={{
                                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                                borderRadius: 1,
                                                p: 1.25,
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1.5,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 500, minWidth: 180 }}>{cf.name}</Typography>
                                            <FormControlLabel
                                                control={<Switch
                                                    checked={cf.required}
                                                    onChange={(e) => {
                                                        setJourneyCustomFields(prev =>
                                                            prev.map(f => f.id === cf.id ? { ...f, required: e.target.checked } : f)
                                                        )
                                                    }}
                                                />}
                                                label="Obrigatório"
                                            />
                                            <FormControlLabel
                                                control={<Switch
                                                    checked={cf.show_in_summary}
                                                    onChange={(e) => {
                                                        setJourneyCustomFields(prev =>
                                                            prev.map(f => f.id === cf.id ? { ...f, show_in_summary: e.target.checked } : f)
                                                        )
                                                    }}
                                                />}
                                                label="Exibir no resumo"
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowRequiredFieldsModal(false)}>Cancelar</Button>
                        <Button onClick={async () => {
                            const normalizedStandardFieldSettings = normalizeStandardFieldSettingsForSave(standardFieldSettings)
                            const payload = {
                                required_opportunity_fields: requiredFields,
                                standard_opportunity_fields_settings: normalizedStandardFieldSettings,
                            }
                            if (board && board.color) payload.color = board.color
                            const enterpriseId = getSession().profile.enterprise.id
                            const journeyId = selectedJourney.id

                            const resp = await api.patch(`api/enterprises/${enterpriseId}/journeys/${journeyId}/`, payload)

                            for (const cf of journeyCustomFields) {
                                await api.patch(`api/enterprises/${enterpriseId}/journeys/${journeyId}/custom_fields/${cf.id}/`, {
                                    required: cf.required,
                                    show_in_summary: cf.show_in_summary,
                                })
                            }

                            if (resp.status === 200) {
                                setBoard(prev => ({
                                    ...prev,
                                    required_opportunity_fields: resp.data.required_opportunity_fields || requiredFields,
                                    standard_opportunity_fields_settings: resp.data.standard_opportunity_fields_settings || normalizedStandardFieldSettings,
                                }))
                                setShowRequiredFieldsModal(false)
                                useNotification('Sucesso', 'Campos da oportunidade atualizados.', 'success')
                            } else {
                                useNotification('Ops', 'Erro ao atualizar campos da oportunidade.', 'danger')
                            }
                        }} variant="contained">Salvar</Button>
                    </DialogActions>
                </Dialog>
                <Menu
                    anchorEl={moreMenuState.anchor}
                    open={Boolean(moreMenuState.anchor)}
                    onClose={() => setMoreMenuState({
                        anchor: null,
                        columnId: null
                    })}
                >
                    <MenuItem onClick={() => {
                        setToMoveColumn({
                            selectedJourney,
                            columnId: moreMenuState.columnId
                        })
                        setMoreMenuState({
                            anchor: null,
                            columnId: null
                        })
                    }}>
                        Mover Coluna
                    </MenuItem>
                    {(() => {
                        const menuColumn = board?.columns?.find(column => column.id === moreMenuState.columnId)
                        const opportunityCount = Number(menuColumn?.opportunity_count ?? menuColumn?.cards?.length ?? 0)
                        return opportunityCount > 0
                    })() &&
                        <MenuItem onClick={() => {
                            setToMoveCards({
                                selectedJourney,
                                origin: moreMenuState.columnId
                            })
                            setMoreMenuState({
                                anchor: null,
                                columnId: null
                            })
                        }}>
                            Mover Oportunidades
                        </MenuItem>
                    }
                    <MenuItem onClick={() => {
                        setToRemove(moreMenuState.columnId)
                        setMoreMenuState({
                            anchor: null,
                            columnId: null
                        })
                    }}>
                        Excluir
                    </MenuItem>
                </Menu>
                {viewMode === "kanban" && !loading &&
                    <Box
                        ref={boardScrollRef}
                        className={`main-journey-board grab-scroll ${isBoardDragging ? 'dragging' : ''} ${edit ? 'hidden' : ''}`}
                        onMouseDownCapture={handleCardDragAutoScrollStart}
                        onMouseDown={handleBoardDragScrollStart}
                    >
                        <>
                            <Board
                                onCardDragEnd={(card, lastPos, nextPos) => {
                                    const dropTarget = getDropTargetInfo(
                                        boardScrollRef.current,
                                        cardDragAutoScroll.current.pointerX,
                                        cardDragAutoScroll.current.pointerY
                                    )
                                    const correctedNextPos = (dropTarget.columnId && dropTarget.columnId !== nextPos.toColumnId)
                                        ? { toColumnId: dropTarget.columnId, toPosition: dropTarget.position }
                                        : nextPos

                                    return moveCard(card, board, lastPos, correctedNextPos, (commit, oldvalue) => {
                                        moveCardCallback(commit, oldvalue, card, lastPos, correctedNextPos, setBoard)
                                    },
                                    ()=>removeColumnCallback(correctedNextPos.toColumnId, setBoard),
                                    ()=>deleteCardCallback(lastPos.fromColumnId, card.id, setBoard)
                                    )
                                }}
                                onColumnDragEnd={(column, _, nextPos) => moveColumn(board, column, nextPos,
                                    (commit, oldvalue) => moveColumnCallback(commit, oldvalue, column, nextPos, setBoard), 
                                    ()=>removeColumnCallback(column?.id, setBoard)
                                )}
                                renderCard={(card, { dragging }) => (
                                    <CardDragAutoScroll
                                        dragging={dragging}
                                        boardScrollRef={boardScrollRef}
                                        cardDragAutoScroll={cardDragAutoScroll}
                                        dropTargetHighlightRef={dropTargetHighlightRef}
                                    >
                                        <LazyRender>
                                            <SingleCard
                                                card={card}
                                                onClick={(column) => {
                                                    setEdit({ card: card, column: column })
                                                    const params = new URLSearchParams(location.search)
                                                    params.set("selectedCard", card.id)
                                                    history.push({
                                                        pathname: location.pathname,
                                                        search: params.toString(),
                                                    })
                                                }}
                                                onCopy={() => openTransfer("copy", card)}
                                                onMove={() => openTransfer("move", card)}
                                                journey={board}
                                                visibleFields={visibleFields}
                                                latestChanged={latestChanged}
                                            />
                                        </LazyRender>
                                    </CardDragAutoScroll>
                                )}
                                renderColumnHeader={({ id, title, description }) => (
                                    <>
                                        <Box className='journey-board-column-header'>
                                            <EditableTitle
                                                key={`column-header-${id}-${title}`}
                                                editable={groups.isSupervisor}
                                                title={title} onChange={(newName) => {
                                                    changeColumnName(newName, id, board, setLoading, 
                                                    (data) => changeColumnNameCallback(data, setBoard),
                                                    ()=>removeColumnCallback(id, setBoard))
                                                }}
                                                automationItem={
                                                    <>
                                                        {enterprise?.has_journey_automation &&
                                                            <AutomationItem
                                                                column={modifiedBoard.columns.find(column => column.id === id)}
                                                                disabled={!groups.isAdmin}
                                                                setShowAutomations={(data) => { groups.isAdmin ? setShowAutomations(data) : null }}
                                                            />
                                                        }
                                                    </>
                                                }
                                                moreOptionsItem={
                                                    <>
                                                        <IconButton
                                                            onClick={(event) =>
                                                            setMoreMenuState({
                                                                anchor: event.currentTarget,
                                                                columnId: id,
                                                            })
                                                        }>
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </>
                                                }
                                            />
                                        </Box>
                                        <Box className='journey-board-column-description'>
                                            <EditableTitle
                                                key={`column-description-${id}-${description}`}
                                                editable={groups.isSupervisor}
                                                title={description}
                                                placeholder={groups.isSupervisor ? "Clique para descrever" : ""}
                                                maxLength={255}
                                                className="editable-description-header"
                                                multiline={true}
                                                minRows={2}
                                                maxRows={4}
                                                onChange={(newDescription) => {
                                                    changeColumnDescription(newDescription, id, board, setLoading,
                                                    (data) => changeColumnDescriptionCallback(data, setBoard),
                                                    ()=>removeColumnCallback(id, setBoard))
                                                }}
                                            />
                                        </Box>
                                        <Box className='journey-board-column-subheader'>
                                            <span>Sumário:</span>
                                            <span>{calcBoardSummary(board, id)}</span>
                                        </Box>
                                    </>
                                )}
                            >{modifiedBoard}
                            </Board>
                        </>
                        {groups.isSupervisor &&
                            <Button
                                className="new-kanban-column-btn"
                                variant='contained'
                                onClick={() => {
                                    createColumn(selectedJourney, (board.columns?.length || 0), setLoading,
                                        (column) => setBoard(addColumn(board, column)))
                                }}
                            >
                                <AddIcon />
                                <Box>Nova Coluna</Box>
                            </Button>}
                    </Box>
                }
                {viewMode === "list" &&
                    <Box className="table-container">
                        <DataGridPro
                            rows={listRows}
                            columns={listColumnsState}
                            disableSelectionOnClick
                            density="compact"
                            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                            components={{ Toolbar: CustomToolbar }}
                            loading={listLoading}
                            pagination
                            paginationMode="server"
                            rowsPerPageOptions={[50, 100, 500]}
                            page={listPage}
                            pageSize={listPageSize}
                            onPageChange={(page) => setListPage(page)}
                            onPageSizeChange={(pageSize) => {
                                setListPageSize(pageSize)
                                setListPage(0)
                            }}
                            rowCount={listRowCount}
                            sortingMode="server"
                            sortModel={listSortModel}
                            onSortModelChange={(sortModel) => setListSortModel(sortModel)}
                            initialState={{
                                pagination: {
                                    pageSize: Number(JOURNEY_LIST_PAGE_SIZE)
                                }
                            }}
                        />
                    </Box>
                }
                {toRemove &&
                    <ConfirmationDialogCaptcha
                        open={toRemove}
                        title="Confirmação"
                        description={
                            <>
                                <Alert severity="warning" sx={{ marginBottom: "1rem" }}>
                                    Ao remover a coluna, todas as oportunidades também serão removidas.
                                </Alert>
                                <Box>
                                    Tem certeza que deseja remover a coluna? Digite: <span style={{ 'fontWeight': 'bold' }}>remover</span> no campo abaixo.
                                </Box>
                            </>
                        }
                        handleClose={() => setToRemove(null)}
                        handleSubmit={() => {
                            removeColumn(toRemove, board, setLoading, ()=>removeColumnCallback(toRemove, setBoard))
                            setToRemove(null)
                        }}
                        captcha={'remover'}
                    />
                }
                {toMoveCards &&
                    <MoveCardsDialog
                        columns={board.columns.filter((c) => c.id !== toMoveCards.origin)}
                        onClose={() => setToMoveCards(null)}
                        onSubmit={async (destination) => {
                            const completed = await moveAllCards(
                                toMoveCards.selectedJourney,
                                toMoveCards.origin,
                                destination,
                                refreshBoardAfterBulkMove
                            )

                            if (completed) setToMoveCards(null)
                            return completed
                        }}
                    />
                }
                {toMoveColumn &&
                        <MoveColumnDialog
                            journeyId={toMoveColumn.selectedJourney.id}
                            onClose={() => setToMoveColumn(null)}
                            onSubmit={async (destination, position) => {
                                await moveJourneyColumn(
                                    toMoveColumn.selectedJourney.id,
                                    toMoveColumn.columnId,
                                    destination,
                                    position,
                                    refreshBoard
                                )
                                setToMoveColumn(null)
                            }}
                        />
                }
                {toRemoveOpportunity &&
                    <ConfirmationDialogCaptcha
                        open={toRemoveOpportunity}
                        title="Confirmação"
                        description={
                            <Box>
                                Tem certeza que deseja remover a oportunidade {toRemoveOpportunity.data.title}? Digite <span style={{ 'fontWeight': 'bold' }}>remover</span> no campo abaixo.
                            </Box>
                        }
                        handleClose={() => setToRemoveOpportunity(null)}
                        handleSubmit={() => {
                            deleteCardFunction(selectedJourney.id, toRemoveOpportunity.data, toRemoveOpportunity.column, setBoard)
                            if (viewMode === "list" && toRemoveOpportunity.data?.id) {
                                setListRows(prev => prev.filter(row => row.id !== toRemoveOpportunity.data.id))
                                setListRowCount(prev => Math.max(0, Number(prev || 0) - 1))
                            }
                            setToRemoveOpportunity(null)
                        }}
                        captcha={'remover'}
                    />
                }
                <TransferOpportunityDialog
                    open={!!transfer}
                    mode={transfer?.mode}
                    sourceJourney={selectedJourney}
                    journeys={journeysList}
                    targetJourneyId={targetJourneyId}
                    setTargetJourneyId={setTargetJourneyId}
                    columns={targetColumns}
                    targetColumnId={targetColumnId}
                    setTargetColumnId={setTargetColumnId}
                    loading={transferLoading}
                    onClose={closeTransfer}
                    onConfirm={handleConfirmTransfer}
                    copyOptions={copyOptions}
                    setCopyOptions={setCopyOptions}
                />
            </Box>
        </JourneyContext.Provider>
    )
}

export default Kanban
